import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, CircleUserRound, LogOut, Settings } from "lucide-react";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";

// Lightweight, fully accessible account disclosure (button + menu). Built on plain
// state so it stays predictable under test; closes on outside-click and Escape.
export function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user?.email ?? "Account"}
        onClick={() => setOpen((prev) => !prev)}
        className="flex min-h-[44px] min-w-0 items-center gap-2 rounded-[var(--radius-md)] border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-3 py-2 text-sm font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)]"
      >
        {/* Below sm the topbar is too narrow for the full email — collapse to an
            avatar so it never overflows the card; the email shows from sm up. */}
        <CircleUserRound
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-[color:var(--text-tertiary)] sm:hidden"
        />
        <span className="hidden max-w-[180px] truncate sm:block">{user?.email ?? "Account"}</span>
        <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-[color:var(--text-tertiary)]" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-[var(--radius-md)] border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] shadow-elevated"
        >
          {/* Identity header — the only place the signed-in email surfaces on
              narrow screens, where the trigger collapses to an avatar. Lets a
              clinician confirm the account before signing out. */}
          <div className="border-b border-[color:var(--border-primary)] px-3 py-2">
            <p className="text-xs text-[color:var(--text-tertiary)]">Signed in as</p>
            <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
              {user?.email ?? "Account"}
            </p>
          </div>
          {/* Story 14.9 (FR84) — the org_admin's ONLY entry point to Settings:
              the sidebar entry was dropped, so this is it. org_admin only —
              /org-admin/settings is behind ORG_ADMIN_ONLY_ROLES, and offering a
              clinician or super_admin a link that 403s is the Franklin#428
              posture we already avoid in the sidebar. */}
          {user?.role === "org_admin" ? (
            <Link
              to="/org-admin/settings"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={cn(
                "flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-[color:var(--text-primary)] transition-colors",
                "hover:bg-[color:var(--bg-tertiary)] focus-visible:outline-none focus-visible:bg-[color:var(--bg-tertiary)]",
              )}
            >
              <Settings aria-hidden="true" className="h-4 w-4" />
              Settings
            </Link>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className={cn(
              "flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-[color:var(--text-primary)] transition-colors",
              "hover:bg-[color:var(--bg-tertiary)] focus-visible:outline-none focus-visible:bg-[color:var(--bg-tertiary)]",
            )}
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
